import { useApp } from '@/contexts';

import { useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { DeleteConfirmDialog, EnvironmentBadge, PageLoader } from '@/components/common';
import { PageContainer, PageHeading } from '@/components/layout';
import { useGetEnvironmentsQuery } from '@/features/environments/api/environmentsApi';
import {
  useGetAppDeploymentsQuery,
  useGetTransformersQuery,
} from '@/features/transformers/api/transformersApi';
import { logger } from '@/utils/logger';

import {
  useClearTriggerSubscriptionMutation,
  useDeleteWebhookSubscriptionMutation,
  useGetTriggerSubscriptionsQuery,
  useGetWebhookSubscriptionsQuery,
} from '../api/eventsApi';
import type { TriggerSubscription, WebhookSubscription } from '../types';

export const EventsSubscriptionsPage = () => {
  const { appId, tenantId } = useApp();
  const navigate = useNavigate();
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<{
    item: WebhookSubscription | TriggerSubscription;
    isObject: boolean;
  } | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const { data: environments } = useGetEnvironmentsQuery(
    { tenantId, appId: appId! },
    { skip: !tenantId || !appId }
  );
  const { data: transformers } = useGetTransformersQuery(appId!, { skip: !appId });
  const { data: deployments } = useGetAppDeploymentsQuery(
    { tenantId, appId: appId! },
    { skip: !tenantId || !appId }
  );

  const { data: webhookSubscriptions, isLoading: isLoadingWebhooks } =
    useGetWebhookSubscriptionsQuery(
      {
        tenantId,
        appId: appId!,
      },
      { skip: !tenantId || !appId }
    );
  const { data: triggerSubscriptions, isLoading: isLoadingTriggers } =
    useGetTriggerSubscriptionsQuery(appId!, { skip: !appId });

  const [deleteWebhookSubscription] = useDeleteWebhookSubscriptionMutation();
  const [clearTriggerSubscription] = useClearTriggerSubscriptionMutation();

  const handleDelete = async () => {
    if (!selectedForDelete) return;

    if (selectedForDelete.isObject) {
      const sub = selectedForDelete.item as WebhookSubscription;
      await deleteWebhookSubscription({
        tenantId,
        appId: appId!,
        subscriptionGuid: sub.data.subscriptionGuid,
      });
    } else {
      const sub = selectedForDelete.item as TriggerSubscription;
      if (sub.BaseId) {
        await clearTriggerSubscription({
          transBaseId: sub.BaseId,
          envId: String(sub.EnvironmentId),
        });
      }
    }
    setDeleteModal(false);
    setSelectedForDelete(null);
  };

  const getObjectName = (fullPath: string): string => {
    const index = fullPath.lastIndexOf('.');
    return fullPath.substring(index + 1);
  };

  const getEnvironmentInfo = (envId: string | undefined) => {
    if (!envId) return undefined;
    return environments?.find((e) => e.id === envId);
  };

  const getRecipeInfo = (baseId: string) => {
    const recipe = transformers?.find((t) => String(t.baseId) === String(baseId));
    return recipe;
  };

  const getDeploymentInfo = (baseId: string, envId: string | undefined) => {
    if (!envId) return undefined;
    return deployments?.find((d) => String(d.baseId) === baseId && d.environmentId === envId);
  };

  const handleCopyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(text);
      setTimeout(() => {
        setCopiedField(null);
      }, 30000);
    } catch (error) {
      logger.error('Failed to copy to clipboard:', error);
    }
  };

  if (isLoadingWebhooks || isLoadingTriggers) {
    return (
      <PageContainer>
        <PageLoader />
      </PageContainer>
    );
  }

  // Group webhook subscriptions by object type
  const groupedWebhooks: Record<string, WebhookSubscription[]> = {};
  webhookSubscriptions?.forEach((sub) => {
    const objType = sub.data.objectTypeFilter;
    if (!groupedWebhooks[objType]) {
      groupedWebhooks[objType] = [];
    }
    groupedWebhooks[objType].push(sub);
  });

  // Group trigger subscriptions by file pattern
  const groupedTriggers: Record<string, TriggerSubscription[]> = {};
  triggerSubscriptions?.forEach((sub) => {
    const pattern = sub.FilePattern;
    if (pattern && !groupedTriggers[pattern]) {
      groupedTriggers[pattern] = [];
    }
    if (pattern) {
      groupedTriggers[pattern].push(sub);
    }
  });

  const hasSubscriptions =
    Object.keys(groupedWebhooks).length > 0 || Object.keys(groupedTriggers).length > 0;

  return (
    <PageContainer>
      <PageHeading
        title="Subscriptions"
        subtitle="Manage webhook and file upload subscriptions"
        action={
          hasSubscriptions ? (
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate(`/a/${appId}/events/sub/new`)}
            >
              New Subscription
            </Button>
          ) : undefined
        }
      />

      {!hasSubscriptions ? (
        <Box
          sx={{
            border: '1px solid #ddd',
            bgcolor: '#f9f9f9',
            p: 2,
            mt: 2,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: '16px', mb: 1 }}>No Subscriptions.</Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate(`/a/${appId}/events/sub/new`)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              mt: 1,
            }}
          >
            New Subscription
          </Button>
        </Box>
      ) : (
        <Box sx={{ position: 'relative', mt: -1, clear: 'both' }}>
          {/* Webhook Object Subscriptions */}
          {Object.entries(groupedWebhooks).map(([objectType, subs]) => (
            <Box key={objectType} sx={{ mb: 3 }}>
              <Box sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: '16px', mb: 1 }}>
                  Subscribed to Object:{' '}
                  <Box component="span" sx={{ fontWeight: 'bold', cursor: 'pointer' }}>
                    {getObjectName(objectType)}
                  </Box>
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {objectType}
                  </Typography>
                  <ContentCopyIcon
                    sx={{
                      fontSize: '16px',
                      cursor: 'pointer',
                      opacity: 0.6,
                      '&:hover': { opacity: 1 },
                    }}
                    onClick={() => handleCopyToClipboard(objectType)}
                  />
                  {copiedField === objectType && (
                    <Typography sx={{ fontSize: '12px', color: 'success.main', ml: 0.5 }}>
                      copied!
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {subs.map((sub, idx) => {
                  const recipe = sub.baseId ? getRecipeInfo(sub.baseId) : null;
                  const env = sub.environmentId ? getEnvironmentInfo(sub.environmentId) : null;
                  const deployment =
                    sub.baseId && sub.environmentId
                      ? getDeploymentInfo(sub.baseId, sub.environmentId)
                      : null;

                  return (
                    <Box
                      key={sub.id || `webhook-${sub.baseId}-${sub.environmentId}-${idx}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        minHeight: '30px',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        position: 'relative',
                        mb: 0.5,
                      }}
                    >
                      {/* Delete icon */}
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          cursor: 'pointer',
                          opacity: 0.6,
                          '&:hover': { opacity: 1 },
                        }}
                        onClick={() => {
                          setSelectedForDelete({ item: sub, isObject: true });
                          setDeleteModal(true);
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: '18px', color: 'error.main' }} />
                      </Box>

                      {sub.baseId ? (
                        // Recipe subscription
                        <>
                          {/* Left color bar for recipe icon */}
                          <Box
                            sx={{
                              width: '22px',
                              bgcolor: '#404040',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Box
                              component="img"
                              src="/static/images/description.svg"
                              sx={{
                                height: '18px',
                                filter: 'invert(100%)',
                              }}
                              onError={(e) => {
                                // Fallback if image doesn't exist
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </Box>

                          {/* Content */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              p: '6px 8px',
                              flex: 1,
                              pr: 4, // Space for delete icon
                            }}
                          >
                            <Typography
                              sx={{
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 400,
                                marginLeft: '5px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                              onClick={() => navigate(`/a/${appId}/tf/${sub.baseId}`)}
                            >
                              {recipe?.name || 'Unknown Recipe'}
                            </Typography>
                            {deployment && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '10px',
                                  display: 'inline-block',
                                  ml: 0.5,
                                }}
                              >
                                ver. {deployment.version}
                              </Typography>
                            )}
                            {deployment && env && (
                              <Typography
                                sx={{ fontSize: '12px', display: 'inline-block', mx: 0.5 }}
                              >
                                -
                              </Typography>
                            )}
                            {env && <EnvironmentBadge name={env.name} color={env.color} />}
                          </Box>
                        </>
                      ) : (
                        // External webhook URL
                        <>
                          {/* Left color bar for webhook icon */}
                          <Box
                            sx={{
                              width: '22px',
                              bgcolor: 'rgb(68,120,178)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Box
                              component="img"
                              src="/static/images/webhookurl.svg"
                              sx={{
                                height: '18px',
                                filter: 'invert(100%)',
                              }}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </Box>

                          {/* Content */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              p: '6px 8px',
                              flex: 1,
                              pr: 4, // Space for delete icon
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '13px',
                                marginLeft: '5px',
                                textDecoration: 'underline',
                                wordBreak: 'break-all',
                              }}
                            >
                              {sub.data.deliveryUrl}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                ml: 1,
                                fontSize: '12px',
                              }}
                            >
                              (External Webhook)
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}

          {/* File Upload Trigger Subscriptions */}
          {Object.entries(groupedTriggers).map(([pattern, subs]) => (
            <Box key={pattern} sx={{ mb: 3 }}>
              <Box sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: '16px', mb: 1 }}>
                  Subscribed to File Upload:{' '}
                  <FileUploadIcon sx={{ height: '18px', verticalAlign: 'middle', mr: 0.5 }} />
                  <Box component="span" sx={{ fontWeight: 'bold', cursor: 'pointer' }}>
                    {pattern}
                  </Box>
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {subs.map((sub, idx) => {
                  if (!sub.BaseId) return null;

                  const recipe = getRecipeInfo(sub.BaseId);
                  const env = getEnvironmentInfo(sub.EnvironmentId);
                  const deployment = getDeploymentInfo(sub.BaseId, sub.EnvironmentId);

                  return (
                    <Box
                      key={sub.id || `trigger-${sub.BaseId}-${sub.EnvironmentId}-${idx}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        minHeight: '30px',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        position: 'relative',
                        mb: 0.5,
                      }}
                    >
                      {/* Delete icon */}
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          cursor: 'pointer',
                          opacity: 0.6,
                          '&:hover': { opacity: 1 },
                        }}
                        onClick={() => {
                          setSelectedForDelete({ item: sub, isObject: false });
                          setDeleteModal(true);
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: '18px', color: 'error.main' }} />
                      </Box>

                      {/* Left color bar for recipe icon */}
                      <Box
                        sx={{
                          width: '22px',
                          bgcolor: '#404040',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          component="img"
                          src="/static/images/description.svg"
                          sx={{
                            height: '18px',
                            filter: 'invert(100%)',
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </Box>

                      {/* Content */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          p: '6px 8px',
                          flex: 1,
                          pr: 4, // Space for delete icon
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '13px',
                            fontWeight: 400,
                            marginLeft: '5px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {recipe?.name || 'Unknown Recipe'}
                        </Typography>
                        {deployment && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '10px',
                              display: 'inline-block',
                              ml: 0.5,
                            }}
                          >
                            ver. {deployment.version}
                          </Typography>
                        )}
                        {deployment && env && (
                          <Typography sx={{ fontSize: '12px', display: 'inline-block', mx: 0.5 }}>
                            -
                          </Typography>
                        )}
                        {env && <EnvironmentBadge name={env.name} color={env.color} />}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteModal}
        onClose={() => {
          setDeleteModal(false);
          setSelectedForDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete Subscription"
        message="Are you sure you want to delete this subscription? This action cannot be undone."
      />
    </PageContainer>
  );
};
